import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, User, LogOut, LayoutDashboard, Store, Briefcase, PlusCircle, Calendar, Package, Users } from 'lucide-react';
import { authApi } from '../services/api';
import UnverifiedUserBanner from './UnverifiedUserBanner';
import NotificationCenter from './NotificationCenter';
// Issue #860: Use RBAC utility functions
import { getCurrentUser } from '../utils/rbac';
import { ROUTES } from '../constants/routes';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export default function Navbar() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(true); // Default to true to avoid flash
  const [showMenu, setShowMenu] = useState(false);
  const [authCheckCounter, setAuthCheckCounter] = useState(0); // Force re-computation on auth changes
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Feature flags
  const { isEnabled: adsEnabled } = useFeatureFlag('vendor_advertisements');
  const { isEnabled: notificationCenterEnabled } = useFeatureFlag('notification_center');
  // Feature flag for blog platform (Issue #1466)
  const { isEnabled: blogEnabled } = useFeatureFlag('blog_enabled');

  // Issue #860: Optimize role checks with useMemo to avoid re-parsing localStorage on every render
  // authCheckCounter is intentionally used as dependency to trigger re-computation on auth changes
  const { adminStatus, fairManagerStatus, vendorStatus } = useMemo(() => {
    const user = getCurrentUser();
    const roles = user?.roles || [];
    return {
      adminStatus: roles.includes('Admin'),
      fairManagerStatus: roles.includes('Fair Manager') || roles.includes('Admin'),
      vendorStatus: roles.includes('Vendor')
    };
  }, [authCheckCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Check login status
    const checkAuth = () => {
      const loggedIn = authApi.isLoggedIn();
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        setUserEmail(localStorage.getItem('user_email') || '');

        // Trigger role check re-computation
        setAuthCheckCounter(prev => prev + 1);

        // Fetch email verification status from user profile (Issue #147)
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            setEmailVerified(user.email_verified ?? true);
          } catch {
            setEmailVerified(true);
          }
        }
      }
    };

    checkAuth();

    // Listen for storage changes (logout in another tab)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleLogout = async () => {
    await authApi.logout();
    setIsLoggedIn(false);
    setShowMenu(false);
    navigate('/');
  };

  const getUserInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  return (
    <>
      <nav className="bg-primary text-white shadow-lg">
        <div className="w-full px-1 py-0">
          {/* Two-row layout on mobile, single row on desktop */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            {/* Row 1: Logo and site title (always visible) */}
            <div className="flex items-center justify-between h-20 md:h-auto">
              <div className="flex items-center gap-4">
                <Link to="/" className="flex items-center">
                  {/* Prominent logo with transparent background and padding for spacing */}
                  <picture>
                    <source srcSet="/logo.webp" type="image/webp" />
                    <img src="/logo.png" alt="Local Artist Fairs" className="h-20 w-auto" />
                  </picture>
                </Link>

                {/* Site title and tagline - visible on desktop only (Issue #1534) */}
                {/* Using div instead of h1 to avoid multiple h1 tags per page (SEO best practice) */}
                <div className="hidden md:flex flex-col gap-1" aria-label="Site branding">
                  <div className="text-base sm:text-xl font-bold text-white" style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }} aria-label="Local Artist Fairs Directory">Local Artist Fairs Directory</div>
                  <p className="text-xs sm:text-sm text-white" style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}>Discover and explore artist fairs across the country</p>
                </div>
              </div>
            </div>

            {/* Row 2 on mobile, inline on desktop: Navigation links */}
            <div className="flex items-center justify-end space-x-3 md:space-x-4 pb-0">
              <Link to={ROUTES.FAIR_CALENDAR} className="flex items-center space-x-1 hover:text-secondary-light text-sm md:text-base">
                <Calendar className="h-4 w-4" />
                <span>Calendar</span>
              </Link>
              <Link to={ROUTES.VENDORS} className="flex items-center space-x-1 hover:text-secondary-light text-sm md:text-base">
                <Users className="h-4 w-4" />
                <span>Vendors</span>
              </Link>
              {blogEnabled && <Link to="/blog" className="hover:text-secondary-light text-sm md:text-base">Blog</Link>}
              <Link to={ROUTES.CONTACT} className="hover:text-secondary-light text-sm md:text-base">Contact</Link>

              {/* Notification Center - only visible when logged in and feature enabled (Issue #496) */}
              {isLoggedIn && notificationCenterEnabled && <NotificationCenter />}

            {isLoggedIn ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center space-x-2 hover:text-secondary-light focus:outline-none"
                  aria-label="User menu"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-dark flex items-center justify-center font-semibold border-2 border-white">
                    {getUserInitials(userEmail)}
                  </div>
                </button>

                {showMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm text-gray-500">Signed in as</p>
                      <p className="text-sm font-medium text-gray-900 truncate">{userEmail}</p>
                      {/* Role badges (Issue #147, Issue #1529) */}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {adminStatus && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-light text-primary">
                            Admin
                          </span>
                        )}
                        {fairManagerStatus && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-light text-primary">
                            Fair Admin
                          </span>
                        )}
                        {vendorStatus && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-light text-primary">
                            Vendor
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Dashboard Links (Issue #147) */}
                    {adminStatus && (
                      <Link
                        to="/admin"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 bg-background"
                        onClick={() => setShowMenu(false)}
                      >
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        Platform Admin Dashboard
                        <span className="ml-auto text-xs text-secondary">Admin</span>
                      </Link>
                    )}
                    {fairManagerStatus && (
                      <Link
                        to="/fairmanager/dashboard"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 bg-moonbone"
                        onClick={() => setShowMenu(false)}
                      >
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        Fair Administrator Dashboard
                        <span className="ml-auto text-xs text-secondary">Fair Admin</span>
                      </Link>
                    )}
                    {(fairManagerStatus || adminStatus) && (
                      <Link
                        to="/fairmanager/fair/new"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowMenu(false)}
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        Create New Fair
                      </Link>
                    )}
                    {vendorStatus && (
                      <Link
                        to="/vendor/dashboard"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 bg-green-50"
                        onClick={() => setShowMenu(false)}
                      >
                        <Store className="h-4 w-4 mr-2" />
                        Vendor Dashboard
                        <span className="ml-auto text-xs text-green-600">Vendor</span>
                      </Link>
                    )}
                    {vendorStatus && adsEnabled && (
                      <Link
                        to="/vendor/advertisements"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowMenu(false)}
                      >
                        <Package className="h-4 w-4 mr-2" />
                        My Advertisements
                      </Link>
                    )}

                    {/* User Options (Issue #147 - always visible) */}
                    <Link
                      to="/reservations"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowMenu(false)}
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      My Reservations
                    </Link>
                    <Link
                      to="/vendor/waitlist"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowMenu(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      My Waitlist
                    </Link>
                    {/* TEMPORARILY DISABLED - ApplicationsStatusDashboard route causing white screen */}
                    {/* <Link
                      to="/my-applications"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowMenu(false)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      My Fair Manager Applications
                    </Link> */}
                    {!fairManagerStatus && (
                      <Link
                        to="/fair-manager-request"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowMenu(false)}
                      >
                        <Briefcase className="h-4 w-4 mr-2" />
                        Become a Fair Manager
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowMenu(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Account Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-200"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3 md:space-x-4">
                <Link to="/signup" className="hover:text-secondary-light flex items-center space-x-1 text-sm md:text-base">
                  <User className="h-4 w-4" />
                  <span>Sign Up</span>
                </Link>
                <Link to="/login" className="hover:text-secondary-light flex items-center space-x-1 text-sm md:text-base">
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </Link>
              </div>
            )}
            </div>
          </div>
        </div>
      </nav>

      {/* Show verification banner for logged-in unverified users */}
      {isLoggedIn && !emailVerified && (
        <UnverifiedUserBanner email={userEmail} />
      )}
    </>
  );
}